import { UseQueryResult, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';

import { 
    useGetBills as useGetBillsOrval, 
    useTriggerPushPayment as useTriggerPushPaymentOrval,
    useGetLinkedNumbers as useGetLinkedNumbersOrval,
    useLinkPhoneNumber as useLinkPhoneNumberOrval,
    useActivatePhoneNumber as useActivatePhoneNumberOrval,
    getGetBillsQueryKey,
    getGetLinkedNumbersQueryKey,
    getBills,
    getLinkedNumbers
} from '@/shared/api/client/payments';
import {CustomerBillDTO, PaymentNumberLinkDTO, PushRequestDTO, PushResponseDTO} from "@/shared/api/model";
import { AxiosResponse } from 'axios';

// Hook for fetching bills
export function useGetBills(): [UseQueryResult<CustomerBillDTO[], Error>, () => void] {
    const queryClient = useQueryClient();
    
    // Use regular useQuery instead of Orval's hook to have better control over types
    const query = useQuery<CustomerBillDTO[], Error, CustomerBillDTO[]>({
        queryKey: getGetBillsQueryKey(),
        queryFn: () => getBills(),
    });
    
    const refetch = () => query.refetch();
    
    return [query, refetch];
}

// Hook for triggering push payment
export function useTriggerPushPayment(): UseMutationResult<PushResponseDTO, Error, PushRequestDTO> {
    const mutation = useTriggerPushPaymentOrval();
    
    // Create a properly typed wrapper
    // @ts-ignore
    const typedMutation: UseMutationResult<PushResponseDTO, Error, PushRequestDTO> = {
        ...mutation,
        mutateAsync: async (data: PushRequestDTO) => {
            const response = await mutation.mutateAsync({ data });
            return response;
        },
        mutate: (data: PushRequestDTO, options: any) => {
            mutation.mutate({ data }, options);
        },
        data: mutation.data,
        error: mutation.error as Error,
        variables: mutation.variables?.data as PushRequestDTO,
        status: mutation.status,
        isError: mutation.isError,
        isIdle: mutation.isIdle,
        isPaused: mutation.isPaused,
        isLoading: mutation.isPending,
        isPending: mutation.isPending,
        isSuccess: mutation.isSuccess,
        reset: mutation.reset,
        context: mutation.context
    };
    
    return typedMutation;
}

// Hook for creating a bill (using the same push payment endpoint)
export function useCreateBill(): UseMutationResult<PushResponseDTO, Error, PushRequestDTO> {
    return useTriggerPushPayment();
}

// Hook for getting linked numbers
export function useGetLinkedNumbers(): [UseQueryResult<PaymentNumberLinkDTO[], Error>, () => void] {
    const queryClient = useQueryClient();
    
    // Use regular useQuery instead of Orval's hook to have better control over types
    const query = useQuery<PaymentNumberLinkDTO[], Error, PaymentNumberLinkDTO[]>({
        queryKey: getGetLinkedNumbersQueryKey(),
        queryFn: () => getLinkedNumbers(),
    });
    
    const refetch = () => query.refetch();
    
    return [query, refetch];
}

// Hook for linking a phone number
export function useLinkPhoneNumber(): UseMutationResult<PaymentNumberLinkDTO, Error, PaymentNumberLinkDTO> {
    const mutation = useLinkPhoneNumberOrval();
    
    // Create a properly typed wrapper
    // @ts-ignore
    const typedMutation: UseMutationResult<PaymentNumberLinkDTO, Error, PaymentNumberLinkDTO> = {
        ...mutation,
        mutateAsync: async (data: PaymentNumberLinkDTO) => {
            const response = await mutation.mutateAsync({ data });
            return response;
        },
        mutate: (data: PaymentNumberLinkDTO, options: any) => {
            mutation.mutate({ data }, options);
        },
        data: mutation.data,
        error: mutation.error as Error,
        variables: mutation.variables?.data as PaymentNumberLinkDTO,
        status: mutation.status,
        isError: mutation.isError,
        isIdle: mutation.isIdle,
        isPaused: mutation.isPaused,
        isLoading: mutation.isPending,
        isPending: mutation.isPending,
        isSuccess: mutation.isSuccess,
        reset: mutation.reset,
        context: mutation.context
    };
    
    return typedMutation;
}

// Hook for activating a phone number
export function useActivatePhoneNumber(): UseMutationResult<PaymentNumberLinkDTO, Error, PaymentNumberLinkDTO> {
    const mutation = useActivatePhoneNumberOrval();
    
    // Create a properly typed wrapper
    // @ts-ignore
    const typedMutation: UseMutationResult<PaymentNumberLinkDTO, Error, PaymentNumberLinkDTO> = {
        ...mutation,
        mutateAsync: async (data: PaymentNumberLinkDTO) => {
            const response = await mutation.mutateAsync({ data });
            return response;
        },
        mutate: (data: PaymentNumberLinkDTO, options: any) => {
            mutation.mutate({ data }, options);
        },
        data: mutation.data,
        error: mutation.error as Error,
        variables: mutation.variables?.data as PaymentNumberLinkDTO,
        status: mutation.status,
        isError: mutation.isError,
        isIdle: mutation.isIdle,
        isPaused: mutation.isPaused,
        isLoading: mutation.isPending,
        isPending: mutation.isPending,
        isSuccess: mutation.isSuccess,
        reset: mutation.reset,
        context: mutation.context
    };
    
    return typedMutation;
}
